import { useMemo } from 'react'
import { sanitizeIconSvg } from './sanitize/icon-svg'

interface InlineIconSvgProps {
  /** Stored icon markup (the `svg` field sanitizeIconSvg produced at save time). */
  svg: string | undefined
  className?: string
  /** Scopes `id`s and local `#id` references so sibling icons cannot collide. */
  idPrefix?: string
}

/**
 * Renders admin-stored icon SVG inline, the same shape #1423's AmenityIcon
 * renders its code-owned Lucide paths (`widgets/amenities/components/
 * AmenityIcon.tsx`): a `viewBox`-only `<svg>` with `aria-hidden` (the
 * surrounding label carries the accessible name) and the sanitized inner
 * markup through `dangerouslySetInnerHTML`.
 *
 * The one difference from AmenityIcon is WHY the sink is acceptable: that
 * markup is a constant; this markup is admin data. It is acceptable here
 * only because `sanitizeIconSvg` runs on it right here, at render time, on
 * every render — not just because it was sanitized when saved (#871 / PR
 * #1415 direction change). Anything the sanitizer rejects renders nothing.
 *
 * Pure: no network. Works under `renderToString` SSR and in the browser.
 */
export function InlineIconSvg({ svg, className, idPrefix }: InlineIconSvgProps) {
  const icon = useMemo(() => (svg ? sanitizeIconSvg(svg, { idPrefix }) : null), [svg, idPrefix])
  if (!icon) return null
  return (
    <svg
      className={className}
      viewBox={icon.viewBox}
      aria-hidden="true"
      focusable="false"
      dangerouslySetInnerHTML={{ __html: icon.markup }}
    />
  )
}
